import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, makeStyles } from '@material-ui/core';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import * as paymentIntegrations from 'paymentIntegrations/paymentIntegrations';
import {
  getCurrencyFormatter,
  getDateFormatter,
  getUISetting,
} from 'reducers/configs/settings';
import Icon from 'components/Icon';
import { modalPages } from 'constants/modalPage';
import { openModalPage } from 'actions/modalPage';
import { getPayments } from 'services/ErplyAPI/sales';
import { getIssuedCoupons } from 'services/ErplyAPI/campaigns';
import { printCoupon, printReceipt } from 'actions/integrations/printer';
import { getIsMSRunning } from 'actions/integrations/printer/utils';
import { SaleDocumentResponse } from 'types/SalesDocument';
import { addError, addWarning, dismissType, dismiss } from 'actions/Error';
import { prepareCouponData } from 'actions/integrations/printer/preparePrintoutData';
import { RootState } from 'reducers';
import { Payment } from 'types/Payment';
import { printMerchantCardholderReceipts } from 'paymentIntegrations/utils';
import { IconName } from 'components/Icon/types';
import { printingStatistics } from 'utils/statistics/PrintingStatistics';
import { STATISTIC_ENTRY_TYPES } from 'utils/statistics/Statistics';

const useStyles = makeStyles({
  toggleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '100%',

      '&:first-child': {
        /** @see {@link https://www.joren.co/flex-grow-9999-hack/} */
        flexGrow: 999,
        minWidth: '300px',
      },
      '&:nth-child(2)': {
        flexGrow: 1,
        minWidth: '238px',
      },
    },
  },
});

type Props = {
  sale: SaleDocumentResponse;
  showName: boolean;
  isSaleFullyReturned: boolean;
};

/**
 * Display sale related details per row: date, time, number, client, sum as well as print, email and start return buttons.
 * ShowName determines if customer name should be shown on the row. It should be passed as false if customer specific sales are opened.
 */

const ToggleRow: React.FC<Props> = ({
  sale,
  showName,
  isSaleFullyReturned,
}) => {
  const [fetchedPayments, setFetchedPayments] = useState<Payment[] | null>(
    null,
  );
  const { t } = useTranslation(['customer', 'payment']);
  const styles = useStyles();
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const dateFormatter = useSelector(getDateFormatter);
  const formatCurrency = useSelector(getCurrencyFormatter);
  const shouldHideRecentSales = useSelector(getUISetting('hideRecentSales'));
  const returnShouldBeHidden =
    isSaleFullyReturned ||
    // Exact exchanges (total: 0.00) don't get payments assigned (since there's nothing to pay for), thus are marked as "UNPAID"
    (sale.paymentStatus === 'UNPAID' && sale.type !== 'CREDITINVOICE');

  async function fetchPaymentsAndCacheThem() {
    printingStatistics.init({
      itemType: STATISTIC_ENTRY_TYPES.TypeNameDownload,
      description: `Getting payment info (docId: ${sale.id}): Fetching payment information from API.`,
    });
    const id = await dispatch(
      addWarning(t('alerts.fetchingPayments', { ns: 'payment' }), {
        dismissible: false,
        errorType: 'fetchingPayments',
      }),
    );
    try {
      const payments = await getPayments({
        documentID: sale.id,
      });
      setFetchedPayments(payments);
      printingStatistics.finishLog(
        `Getting payment info (docId: ${sale.id}): Fetching payment information from API.`,
      );
      return payments;
    } catch (e) {
      const msg: string = (() => {
        if ('message' in e) {
          return e.message;
        }
        if (typeof e === 'string') {
          return e;
        }
        return '';
      })();
      // eslint-disable-next-line no-console
      console.error(
        'Failed to fetch payment information for printing. Error: ',
        e?.message ?? e,
      );
      dispatch(
        addError(
          t('alerts.fetchingPayments', { ns: 'payment', context: 'failed' }),
        ),
      );
      printingStatistics.finishLog(
        `Getting payment info (docId: ${sale.id}): Fetching payment information from API.`,
        msg,
      );
      setFetchedPayments(null);
      return [];
    } finally {
      dispatch(dismiss(id));
    }
  }

  const print = async ({ giftReceipt = false } = {}) => {
    printingStatistics.init({
      description: 'Total print time',
      itemType: STATISTIC_ENTRY_TYPES.TypeNameTotalTime,
    });
    const isReceiptPrintoutAPIRunning = await dispatch(
      getIsMSRunning('receipt-printout-api'),
    );
    printingStatistics.init({
      itemType: STATISTIC_ENTRY_TYPES.TypeNameDownload,
      description: `Getting payment info (docId: ${sale.id})`,
    });
    const receiptAPINotRunningAndPaymentsCached =
      !isReceiptPrintoutAPIRunning && fetchedPayments;
    // Already has payments cached
    if (receiptAPINotRunningAndPaymentsCached) {
      printingStatistics.finishLog(
        `Getting payment info (docId: ${sale.id})`,
        'Payment info already cached',
      );
    }
    // do not fetch payments if receipt-printout-api MS is running
    // receipt-printout-api MS only needs sales document id for printing
    const payments = !isReceiptPrintoutAPIRunning
      ? fetchedPayments ?? (await fetchPaymentsAndCacheThem())
      : [];
    if (isReceiptPrintoutAPIRunning) {
      printingStatistics.finishLog(
        `Getting payment info (docId: ${sale.id})`,
        'Getting payment info relayed to receipt-printout-api',
      );
    } else if (!receiptAPINotRunningAndPaymentsCached) {
      printingStatistics.finishLog(`Getting payment info (docId: ${sale.id})`);
    }
    // @ts-ignore
    dispatch(printReceipt({ payments, ...sale }, { giftReceipt })).finally(
      () => {
        dispatch(
          printingStatistics.finishLogAndSendToMetrics('Total print time'),
        );
      },
    );
  };

  const printCardPaymentReceipts = async () => {
    const payments = fetchedPayments ?? (await fetchPaymentsAndCacheThem());
    if (!payments || !payments.length) {
      return;
    }

    const paymentsThatHaveReceipts = payments.filter(p => {
      const {
        merchantPatchScript: merchant,
        cardholderPatchScript: cardholder,
      } = p.jdoc?.terminalResponse?.brazilMeta ?? {};
      const hasReceipt = !!(cardholder || merchant);
      return hasReceipt;
    });
    // If receipts not present - check why
    if (!paymentsThatHaveReceipts.length) {
      const usedIntegrationsDontSupportReprint = payments.every(p => {
        const { integrationName } = p.jdoc?.terminalResponse?.brazilMeta ?? {};
        return (
          integrationName &&
          !paymentIntegrations[integrationName]?.supportsRePrinting
        );
      });
      // All the integrations used do not support reprinting
      if (usedIntegrationsDontSupportReprint) {
        dispatch(
          addWarning(
            t('alerts.rePrintPaymentsNotYetSupported', { ns: 'payment' }),
          ),
        );
        return;
      }
      // Cardholder/merchant are simply not present
      dispatch(
        addWarning(
          t('alerts.printPaymentsNoPaymentsWithJdoc', { ns: 'payment' }),
        ),
      );
      return;
    }

    paymentsThatHaveReceipts.forEach(p => {
      const {
        terminalResponse: {
          brazilMeta: {
            merchantPatchScript: merchant,
            cardholderPatchScript: cardholder,
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } = p.jdoc!; // <- we know this is defined since it's filtered out prior

      dispatch(
        printMerchantCardholderReceipts({
          merchant,
          cardholder,
        }),
      );
    });
  };

  const printSaleRelatedCoupons = async () => {
    dispatch(
      addWarning(t('recentPurchases.coupons.fetching'), {
        errorType: 'recent-sales-coupon-fetching',
      }),
    );

    // If opened from recent sales, showName is true
    const issuedCouponsForSale = await getIssuedCoupons({
      issuedInvoiceIDs: String(sale.id),
    })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch issued coupons for sale', sale, err);
        dispatch(addError(t('recentPurchases.coupons.error')));
      })
      .finally(() => dispatch(dismissType('recent-sales-coupon-fetching')));

    if (!issuedCouponsForSale || !issuedCouponsForSale.length) {
      return dispatch(
        addWarning(t('recentPurchases.coupons.none'), {
          selfDismiss: 4000,
          dismissible: true,
        }),
      );
    }

    return issuedCouponsForSale.forEach(
      async ({ uniqueIdentifier, couponID }) => {
        dispatch(
          addWarning(t('recentPurchases.coupons.preparing'), {
            errorType: 'recent-sales-coupon-preparing',
          }),
        );
        const data = await dispatch(
          prepareCouponData({
            uniqueIdentifier,
            couponID: Number(couponID),
          }),
        );
        dispatch(dismissType('recent-sales-coupon-preparing'));
        dispatch(
          addWarning(t('recentPurchases.coupons.printing'), {
            errorType: 'recent-sales-coupon-printing',
          }),
        );
        await dispatch(printCoupon(data));
        dispatch(dismissType('recent-sales-coupon-printing'));
      },
    );
  };

  const sendEmail = () => {
    dispatch(
      openModalPage({
        component: modalPages.emailModal,
        isPopup: true,
        props: {
          customerEmail: sale.clientEmail,
          invoiceID: sale.id,
        },
      }),
    );
  };

  type IconButton = {
    title: string;
    iconName: IconName;
    testId: string;
    action: () => Promise<void | number> | void;
    style?: React.CSSProperties;
  };
  const printReceiptButton: IconButton = {
    title: 'printReceipt',
    iconName: 'icon_printer',
    testId: 'print-receipt',
    action: () => print(),
    style: {
      display: !shouldHideRecentSales ? 'block' : 'none',
    },
  };

  const printGiftReceiptButton: IconButton = {
    title: 'printGiftReceipt',
    iconName: 'icon_gift_alt',
    testId: 'print-gift-receipt',
    action: () => print({ giftReceipt: true }),
  };

  const couponPrintButton: IconButton = {
    title: 'coupon',
    iconName: 'icon_tags_alt',
    testId: 'print-coupon',
    action: () => printSaleRelatedCoupons(),
    style: {
      display: sale.issuedCouponIDs?.length ? 'block' : 'none',
      transform: 'scaleX(-1)',
    },
  };
  const sendEmailButton: IconButton = {
    title: 'emailReceipt',
    iconName: 'icon_mail_alt',
    testId: 'email-receipt',
    action: () => sendEmail(),
    style: {
      display: sale.clientEmail ? 'block' : 'none',
    },
  };
  const printCardPaymentsButton: IconButton = {
    title: 'printCardPaymentReceipts',
    iconName: 'icon_creditcard',
    testId: 'print-payments',
    action: () => printCardPaymentReceipts(),
  };

  const buttons = [
    printReceiptButton,
    printGiftReceiptButton,
    couponPrintButton,
    sendEmailButton,
    printCardPaymentsButton,
  ];

  return (
    <div className={styles.toggleRow}>
      <div>
        <div>
          <div>
            <strong
              data-testid="sale-number"
              data-test-key={`sale-number-${sale.number}`}
            >
              {sale.number}
            </strong>
            {showName && (
              <strong
                data-testid="client-name"
                data-test-key={`${sale.clientName}-${sale.number}`}
                className="ml-4"
              >
                {sale.clientName}
              </strong>
            )}
          </div>
          <div>
            <span
              data-testid="sale-date"
              data-test-key={`sale-date-${sale.number}`}
              className="mr-2"
            >
              {dateFormatter(sale.date)}
            </span>
            <span
              data-testid="sale-time"
              data-test-key={`sale-time-${sale.number}`}
              className="mr-2"
            >
              {sale.time}
            </span>
          </div>
        </div>
        <div>
          <strong
            data-testid="sale-total"
            data-test-key={`sale-total-${sale.number}`}
          >
            {formatCurrency(sale.total)}
          </strong>
        </div>
      </div>
      <div>
        <Box
          display="flex"
          alignItems="start"
          marginX="0.5rem"
          gridGap="0.5rem"
          width={`calc(${buttons.length} * 24px + ${buttons.length -
            1} * 0.5rem)`}
          onClick={e => e.stopPropagation()}
        >
          {buttons.map(button => {
            return (
              <Icon
                title={t(`customerView.buttons.${button.title}`)}
                invert
                data-testid={button.testId}
                data-test-key={`${button.testId}-${sale.number}`}
                name={button.iconName}
                action={button.action}
                style={button.style}
              />
            );
          })}
        </Box>
        <Button
          variant="contained"
          color="secondary"
          data-testid="start-return"
          data-test-key={`start-return-${sale.number}`}
          style={{
            visibility: returnShouldBeHidden ? 'hidden' : 'visible',
          }}
          onClick={e => {
            e.stopPropagation();
            dispatch(
              openModalPage({
                props: {
                  saleNumberProp: sale.number,
                },
                component: modalPages.ProductReturn,
                modalClassName: 'product-return-modal',
              }),
            );
          }}
        >
          <span>{t('customerView.buttons.startReturn')}</span>
        </Button>
      </div>
    </div>
  );
};

export default ToggleRow;
